/**
 * AI 图像分析批量工具页面
 * 支持同时选择多种分析类型（标题生成、侵权检测、提示词反推），
 * 提交后创建一个批量任务，N 张图 × M 种工具 = N×M 个队列项。
 */

import { useCallback, useState } from 'react'
import type { ReactElement } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, ClipboardList, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MultiImageUploadField } from '@/components/workflow/form-fields/MultiImageUploadField'
import { COMPONENT_TYPES, COMPONENT_CATEGORIES } from '@/types/workflow'
import type { FormComponent } from '@/types/workflow'
import { useUploadStore } from '@/stores/upload'
import { submitImageToTextTask } from '@/services/imageToTextTaskService'
import type { ImageToTextToolType } from '@/types/imageToTextTask'

// 上传组件的静态配置
const UPLOAD_COMPONENT: FormComponent = {
  id: 'tool-images',
  component_type: COMPONENT_TYPES.MULTI_IMAGE_UPLOAD,
  component_category: COMPONENT_CATEGORIES.IMAGE,
  title: '上传图片',
  param_name: 'images',
  node_id: null,
  node_field: null,
  is_required: false,
  default_value: [],
  options: null,
  config: null,
  flags: null,
  sort_order: 0,
}

const TOOL_OPTIONS: Array<{
  toolType: ImageToTextToolType
  icon: string
  label: string
  description: string
}> = [
  {
    toolType: 'product_title',
    icon: '🏷️',
    label: '标题生成',
    description: '生成 Temu 英文商品标题',
  },
  {
    toolType: 'infringement',
    icon: '🛡️',
    label: '侵权检测',
    description: '检测图片是否含侵权 IP',
  },
  {
    toolType: 'reverse_prompt',
    icon: '🔍',
    label: '提示词反推',
    description: '反推 AI 绘图提示词',
  },
]

export function ImageToTextToolPage(): ReactElement {
  const navigate = useNavigate()

  // All 3 tools selected by default
  const [selectedTools, setSelectedTools] = useState<Set<ImageToTextToolType>>(
    new Set(['product_title', 'infringement', 'reverse_prompt'])
  )
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const sessions = useUploadStore((state) => state.sessions)

  const buildUrlFilenameMap = useCallback((): Map<string, string> => {
    const map = new Map<string, string>()
    for (const session of Object.values(sessions)) {
      for (const item of Object.values(session.filesById)) {
        if (item.url !== undefined && item.url !== '') {
          map.set(item.url, item.file.name)
        }
      }
    }
    return map
  }, [sessions])

  const toggleTool = useCallback((toolType: ImageToTextToolType): void => {
    setSelectedTools((prev) => {
      const next = new Set(prev)
      if (next.has(toolType)) {
        next.delete(toolType)
      } else {
        next.add(toolType)
      }
      return next
    })
  }, [])

  const imageCount = uploadedUrls.length
  const toolCount = selectedTools.size
  const estimatedItems = imageCount * toolCount

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (uploadedUrls.length === 0 || selectedTools.size === 0) return

    setIsSubmitting(true)
    try {
      const urlFilenameMap = buildUrlFilenameMap()
      const images = uploadedUrls.map((url) => ({
        url,
        original_filename: urlFilenameMap.get(url) ?? url.split('/').pop() ?? url,
      }))

      const result = await submitImageToTextTask({
        tool_types: Array.from(selectedTools),
        images,
      })

      toast.success(`已提交任务，共 ${String(result.total_count)} 项，正在后台处理`)
      void navigate(`/records/tools/${result.id}`)
    } catch (err) {
      const message = err instanceof Error ? err.message : '提交失败，请重试'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }, [uploadedUrls, selectedTools, buildUrlFilenameMap, navigate])

  return (
    <div className="space-y-6">
      {/* 页面头部 */}
      <div className="flex items-start gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/workflows" aria-label="返回工具列表">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-2xl leading-none">🔬</span>
            <h1 className="text-2xl font-bold tracking-tight">AI 图像分析</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            批量上传图片，同时进行标题生成、侵权检测、提示词反推
          </p>
        </div>
      </div>

      {/* 主内容区 - 左右分栏（上传 2 : 配置+提交 3） */}
      <div className="grid gap-6 lg:grid-cols-5">

        {/* 左侧：上传区域 */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border bg-card p-5 shadow-sm">
            <MultiImageUploadField
              component={UPLOAD_COMPONENT}
              value={uploadedUrls}
              onChange={(v): void => { setUploadedUrls(v as string[]) }}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* 右侧：工具选择 + 预估 + 提交 + 记录 */}
        <div className="space-y-4 lg:col-span-3">

          {/* 分析类型选择 */}
          <div className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="space-y-3">
              <p className="text-sm font-medium">选择分析类型</p>
              <div className="space-y-2.5">
                {TOOL_OPTIONS.map((option) => (
                  <label
                    key={option.toolType}
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-1 py-0.5 transition-colors hover:bg-muted/40"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTools.has(option.toolType)}
                      onChange={(): void => { toggleTool(option.toolType) }}
                      disabled={isSubmitting}
                      className="h-4 w-4 rounded border-border accent-primary"
                    />
                    <span className="flex items-center gap-1.5 text-sm">
                      <span>{option.icon}</span>
                      <span className="font-medium">{option.label}</span>
                      <span className="text-xs text-muted-foreground">{option.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* 算力预估 */}
          {(imageCount > 0 || toolCount > 0) && (
            <div className="rounded-2xl border bg-muted/30 px-4 py-3">
              <p className="text-sm text-muted-foreground">
                预计消耗{' '}
                <span className="font-semibold text-foreground">{imageCount}</span>
                {' × '}
                <span className="font-semibold text-foreground">{toolCount}</span>
                {' × 1 = '}
                <span className="font-semibold text-primary">{estimatedItems}</span>
                {' 算力'}
              </p>
            </div>
          )}

          {/* 提交卡片 */}
          <div className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                上传图片并选择分析类型后点击提交，任务将在后台队列中处理，完成后可在任务记录中查看结果并导出 CSV。
              </p>
              <Button
                className="w-full"
                disabled={uploadedUrls.length === 0 || selectedTools.size === 0 || isSubmitting}
                onClick={(): void => { void handleSubmit() }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    提交中…
                  </>
                ) : uploadedUrls.length === 0 ? (
                  '请先上传图片'
                ) : selectedTools.size === 0 ? (
                  '请选择至少一种分析类型'
                ) : (
                  `提交任务（${imageCount.toLocaleString()} 张 × ${toolCount} 类 = ${estimatedItems.toLocaleString()} 项）`
                )}
              </Button>
            </div>
          </div>

          {/* 任务记录入口 */}
          <div className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">任务记录</p>
                <p className="mt-0.5 text-xs text-muted-foreground">查看历史提交的任务及结果</p>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/records/tools">
                  <ClipboardList className="mr-1.5 h-3.5 w-3.5" />
                  查看记录
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
